import { DateStringValue } from '@unistocks/components/calendar/types';
import { useUncontrolled } from '@unistocks/components/date-picker/hooks/use-uncontrolled';
import { DatePickerType, DatePickerValue, DateValue } from '@unistocks/components/date-picker/types';
import { createDate } from '@unistocks/shared/utils/util';
import { DateTime } from 'luxon';
import { useState } from 'react';

/**
 * The STORED (converted) value for a picker `type` — every date normalised to a
 * `yyyy-MM-dd` (or `yyyy-MM-dd HH:mm:ss` with `withTime`) string: `range` →
 * `[start, end]`, `multiple` → `string[]`, `default` → `string | null`.
 */
type DatesValue<Type extends DatePickerType> = DatePickerValue<Type, DateStringValue>;

/** Input options — mirrors Mantine's `UseUncontrolledDates`, but `any`-free. */
export interface UseUncontrolledDatesInput<Type extends DatePickerType = 'default'> {
  /** Picker selection mode. */
  type: Type;

  /** Controlled value (raw `Date`/string), or `undefined` for uncontrolled. */
  value: DatePickerValue<Type> | undefined;

  /** Default value for uncontrolled mode (raw `Date`/string). */
  defaultValue: DatePickerValue<Type> | undefined;

  /** Called with the converted (string) value when it changes. */
  onChange: ((value: DatesValue<Type>) => void) | undefined;

  /** Convert to `yyyy-MM-dd HH:mm:ss` instead of `yyyy-MM-dd`. @default false */
  withTime?: boolean;
}

/** The empty value for a picker `type` (`[null, null]` / `[]` / `null`). */
const getEmptyValue = <Type extends DatePickerType>(type: Type): DatesValue<Type> =>
  (type === 'range' ? [null, null] : type === 'multiple' ? [] : null) as DatesValue<Type>;

/**
 * Normalise a raw picker value (scalar or array of `Date`/strings) to its stored
 * string form via the date/date-time converter. `undefined` passes through so
 * controlled/uncontrolled detection in `useUncontrolled` still works.
 */
export function convertDatesValue<Type extends DatePickerType>(
  value: DatePickerValue<Type> | undefined,
  withTime: boolean
): DatesValue<Type> | undefined {
  if (value === undefined) {
    return undefined;
  }
  const format = `yyyy-MM-dd${withTime ? ' HH:mm:ss' : ''}`;

  // `createDate` strips the time via `.startOf('day')`, so for the `withTime`
  // case parse with Luxon directly to keep the clock component.
  const toDateTime = (item: DateValue): DateTime =>
    withTime && typeof item === 'string' ? DateTime.fromISO(item) : createDate(item)!;

  return (
    Array.isArray(value)
      ? value.map(item => toDateTime(item).toFormat(format))
      : toDateTime(value).toFormat(format)
  ) as DatesValue<Type>;
}

/**
 * Controlled/uncontrolled value state for the date pickers — the `any`-free port
 * of Mantine's `useUncontrolledDates`. Wraps `@knitui/hooks`' `useUncontrolled`,
 * converting raw `Date`/string inputs to canonical `yyyy-MM-dd` strings on the
 * way in (so stored state is always string-shaped) and resetting the value when
 * the picker `type` changes between renders.
 *
 * Returns `[value, setValue, controlled]` with `value` precisely typed by `type`.
 */
export function useUncontrolledDates<Type extends DatePickerType = 'default'>({
  type,
  value,
  defaultValue,
  onChange,
  withTime = false
}: UseUncontrolledDatesInput<Type>): [
  DatesValue<Type>,
  (value: DatesValue<Type>) => void,
  boolean
] {
  const [storedType, setStoredType] = useState<Type>(type);
  const [_value, _setValue, controlled] = useUncontrolled<DatesValue<Type>>({
    value: convertDatesValue(value, withTime),
    defaultValue: convertDatesValue(defaultValue, withTime),
    finalValue: getEmptyValue(type),
    onChange
  });

  let _finalValue = _value;

  if (storedType !== type) {
    setStoredType(type);

    if (value === undefined) {
      _finalValue =
        defaultValue !== undefined
          ? (convertDatesValue(defaultValue, withTime) ?? getEmptyValue(type))
          : getEmptyValue(type);
      _setValue(_finalValue);
    }
  }

  return [_finalValue, _setValue, controlled];
}
